import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ApiError } from '@/shared/api/client'
import { useApiClient } from '@/shared/api/use-api-client'
import { loadMore, type CursorPage } from '@/shared/api/paging'

import { sortFieldOf, type SearchDefinition } from './definition'
import { toApiQuery, type FieldKind, type SearchState, type SortState } from './search-state'

/** How long typing settles before the search runs. */
export const searchDelayMs = 300

export interface SearchResults<T> {
  /** `forbidden` when the API answers 403: the viewer may not read this list. */
  readonly status: 'loading' | 'ready' | 'error' | 'forbidden'
  /** The API's error code when the status is `error`, if it gave one. */
  readonly errorCode: string | null
  readonly rows: readonly T[]
  /** The row ids of each loaded page, each kept as the same array, for contributed columns. */
  readonly pages: readonly (readonly string[])[]
  readonly hasMore: boolean
  readonly loadingMore: boolean
  readonly moreFailed: boolean
  readonly loadMore: () => void
  readonly retry: () => void
  /**
   * The API query the shown results were loaded with, relative periods resolved; `null` until
   * they load. Later pages and the export reuse it, so they match what is shown.
   */
  readonly query: string | null
}

interface Loaded<T> {
  readonly key: string
  readonly query: string
  readonly page: CursorPage<T>
  readonly pages: readonly (readonly string[])[]
}

/** The screen's sort: the viewer's, else the definition's default, else its first sortable column's. */
export function useSort<T>(definition: SearchDefinition<T>, state: SearchState): SortState | null {
  const { defaultSort, columns } = definition
  return useMemo(() => {
    const first = columns.map((column) => sortFieldOf(column)).find((field) => field !== undefined)
    return state.sort ?? defaultSort ?? (first === undefined ? null : { field: first, descending: false })
  }, [state.sort, defaultSort, columns])
}

/**
 * Builds the API query a search sends, for its list and its export: relative periods resolve
 * against the clock when it is called.
 */
export function useApiQuery<T>(
  definition: SearchDefinition<T>,
  state: SearchState,
  timeZone: string,
): () => URLSearchParams {
  const kinds = useMemo<Readonly<Record<string, FieldKind>>>(
    () => Object.fromEntries(definition.filters.map((field) => [field.id, field.kind])),
    [definition.filters],
  )
  const sort = useSort(definition, state)
  return useCallback(() => toApiQuery({ ...state, sort }, kinds, timeZone, new Date()), [state, sort, kinds, timeZone])
}

function withQuery(path: string, query: string): string {
  return query === '' ? path : `${path}?${query}`
}

/**
 * Loads a search's first page whenever its query changes, after typing settles, and further
 * pages on "Load more" (ADR-API0003: a cursor, no page numbers). A newer query cancels an older
 * one, so results never arrive out of order.
 */
export function useSearchResults<T>(
  definition: SearchDefinition<T>,
  state: SearchState,
  timeZone: string,
): SearchResults<T> {
  const client = useApiClient()
  const apiQuery = useApiQuery(definition, state, timeZone)
  const { path, parseRow, rowId, pageSize } = definition
  // From the state, not the API query: a relative period's range moves with the clock.
  const key = JSON.stringify([path, state.q.trim(), state.filters, state.sort, state.includeInactive, timeZone])
  const [loaded, setLoaded] = useState<Loaded<T> | null>(null)
  const [failed, setFailed] = useState<{ readonly key: string; readonly isForbidden: boolean; readonly code: string | null } | null>(null)
  const [attempt, setAttempt] = useState(0)
  // Kept with the query it belongs to, so a new search never shows another's Load more state.
  const [pendingMore, setPendingMore] = useState<{ readonly key: string; readonly failed: boolean } | null>(null)
  const loadingMore = pendingMore?.key === key && !pendingMore.failed
  const moreFailed = pendingMore?.key === key && pendingMore.failed
  // Kept current without restarting a search: the client changes when sign-in settles.
  const query = useRef(apiQuery)
  const api = useRef(client)
  const moreAbort = useRef<AbortController | null>(null)
  const first = useRef(true)
  const lastAttempt = useRef(attempt)

  useEffect(() => {
    query.current = apiQuery
    api.current = client
  }, [apiQuery, client])

  useEffect(() => {
    const controller = new AbortController()
    // The first load and a retry run at once; a changed query waits for typing to settle.
    const retried = attempt !== lastAttempt.current
    lastAttempt.current = attempt
    const delay = first.current || retried ? 0 : searchDelayMs
    first.current = false

    async function search() {
      try {
        const resolved = query.current().toString()
        const page = await loadMore(api.current, withQuery(path, resolved), null, parseRow, {
          limit: pageSize,
          signal: controller.signal,
        })
        setLoaded({ key, query: resolved, page, pages: [page.data.map((row) => rowId(row))] })
        setFailed(null)
        setPendingMore(null)
      } catch (error: unknown) {
        // The client has logged it.
        if (!controller.signal.aborted) {
          const apiError = error instanceof ApiError ? error : null
          setFailed({ key, isForbidden: apiError?.status === 403, code: apiError?.code ?? null })
        }
      }
    }

    const timer = setTimeout(() => void search(), delay)
    // A new query or leaving the page cancels this search and any "Load more" still running.
    return () => {
      clearTimeout(timer)
      controller.abort()
      moreAbort.current?.abort()
    }
  }, [key, attempt, path, parseRow, rowId, pageSize])

  const more = useCallback(() => {
    if (loaded === null || !loaded.page.pagination.hasMore || loadingMore) {
      return
    }
    const controller = new AbortController()
    moreAbort.current = controller
    setPendingMore({ key: loaded.key, failed: false })
    const shown = loaded.page.data.length

    async function next(current: Loaded<T>) {
      try {
        const page = await loadMore(api.current, withQuery(path, current.query), current.page, parseRow, {
          limit: pageSize,
          signal: controller.signal,
        })
        setLoaded((latest) =>
          latest?.key === current.key
            ? { ...latest, page, pages: [...latest.pages, page.data.slice(shown).map((row) => rowId(row))] }
            : latest,
        )
        setPendingMore(null)
      } catch {
        if (!controller.signal.aborted) {
          setPendingMore({ key: current.key, failed: true })
        }
      }
    }

    void next(loaded)
  }, [loaded, loadingMore, path, parseRow, rowId, pageSize])

  // Back to loading at once, so the error and its retry button go while the new attempt runs.
  const retry = useCallback(() => {
    setFailed(null)
    setAttempt((count) => count + 1)
  }, [])

  const current = loaded?.key === key ? loaded : null
  const failure = failed?.key === key ? failed : null
  return {
    status: failure !== null ? (failure.isForbidden ? 'forbidden' : 'error') : current === null ? 'loading' : 'ready',
    errorCode: failure === null || failure.isForbidden ? null : failure.code,
    rows: current?.page.data ?? noRows,
    pages: current?.pages ?? noPages,
    hasMore: current?.page.pagination.hasMore ?? false,
    loadingMore,
    moreFailed,
    loadMore: more,
    retry,
    query: current?.query ?? null,
  }
}

const noRows: readonly never[] = []
const noPages: readonly (readonly string[])[] = []
